import { getCurrency, currencyLabel } from '@/lib/currency';
import type { DashboardData } from './types';

export const formatNumber = (value: number | null, unit: 'number' | 'eur' = 'number') =>
  value === null
    ? '—'
    : new Intl.NumberFormat('fr-FR', {
        maximumFractionDigits: Number.isInteger(value) ? 0 : 1,
        ...(unit === 'eur'
          ? { style: 'currency', currency: getCurrency(), maximumFractionDigits: 0 }
          : {}),
      }).format(value);
export const formatPercent = (value: number | null) =>
  value === null ? '—' : `${formatNumber(value)} %`;
export const CHART_COLORS = [
  '#7452ce',
  '#427aca',
  '#279487',
  '#ce9556',
  '#ad6d94',
  '#6b809f',
  '#9a7be0',
  '#5aa6b7',
  '#b8784c',
  '#708143',
  '#5861b8',
  '#aa7384',
  '#647b8f',
];
export const chartColor = (index: number) => CHART_COLORS[index % CHART_COLORS.length]!;

// Protect spreadsheet consumers from formula execution in labels supplied by users.
const cell = (value: string | number | null) => {
  let text = value === null ? '' : String(value);
  if (typeof value === 'string' && /^\s*[=+@-]/u.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
};
export const dashboardCsv = (data: DashboardData, owner = 'Tous les responsables') => {
  const rows: (string | number | null)[][] = [
    ['ubeefy', data.section],
    ['Depuis', data.from ?? 'Toute la période'],
    ['Jusqu’au', data.to],
    ['Responsable', owner],
    ['Actualisé le', data.generatedAt],
    [],
    ['Indicateur', 'Valeur', 'Part (%)', 'Définition', 'Évolution (%)', 'Comparaison'],
    ...data.metrics.map((metric) => [
      metric.label,
      metric.value,
      metric.share,
      metric.detail,
      metric.change ?? null,
      metric.comparison ?? '',
    ]),
  ];
  if (data.ratios.length) rows.push([], ['Ratio', 'Numérateur', 'Dénominateur', 'Taux (%)']);
  for (const ratio of data.ratios)
    rows.push([ratio.label, ratio.numerator, ratio.denominator, ratio.value]);
  for (const chart of data.charts) {
    rows.push([], [chart.title], [chart.description]);
    if (chart.kind === 'line') {
      rows.push(['Période', ...chart.series.map((series) => series.label)]);
      chart.labels.forEach((label, i) =>
        rows.push([label, ...chart.series.map((series) => series.values[i] ?? 0)]),
      );
    } else if (chart.kind === 'stacked') {
      rows.push(['Profil', ...chart.series.map((series) => series.label)]);
      chart.rows.forEach((row) => rows.push([row.label, ...(row.values ?? [])]));
    } else {
      rows.push([
        'Libellé',
        chart.unit === 'eur' ? currencyLabel('Valeur (€)') : 'Valeur',
        'Minimum',
        'Maximum',
        'Échantillon',
      ]);
      chart.rows.forEach((row) =>
        rows.push([row.label, row.value, row.min ?? null, row.max ?? null, row.sample ?? null]),
      );
    }
  }
  rows.push([], ['Lecture des indicateurs'], ...data.notes.map((note) => [note]));
  return '\uFEFF' + rows.map((row) => row.map(cell).join(';')).join('\r\n');
};

export const downloadDashboard = (data: DashboardData, owner: string) => {
  const url = URL.createObjectURL(
    new Blob([dashboardCsv(data, owner)], { type: 'text/csv;charset=utf-8;' }),
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = `ubeefy-dashboard-${data.section}-${data.to}.csv`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
